import React from "react";
import { X, AlertTriangle, Download } from "lucide-react";
import ExcelJS from "exceljs";
import styles from "./ImportErrorModal.module.css";

// Define the shape of the error details
interface ErrorDetail {
  row: number;
  field?: string | null;
  email?: string | null;
  message: string;
}

interface ImportErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorDetails: ErrorDetail[];
  fileName?: string;
}

const ImportErrorModal: React.FC<ImportErrorModalProps> = ({
  isOpen,
  onClose,
  errorDetails,
  fileName,
}) => {
  if (!isOpen) return null;

  const handleDownload = async () => {
    if (errorDetails.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'CoWonder';
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet('Clientes com Erro');
    worksheet.columns = [
      { header: 'Linha Original', key: 'row', width: 15 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Campo com Erro', key: 'field', width: 20 },
      { header: 'Mensagem de Erro', key: 'message', width: 50 },
    ];

    errorDetails.forEach((detail) => {
      worksheet.addRow({
        row: detail.row,
        email: detail.email || 'N/A',
        field: detail.field || 'N/A',
        message: detail.message,
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDC2626' }, // Red background for header
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const safeBaseName = (fileName || 'importacao')
      .replace(/\.[^/.]+$/, '')
      .replace(/[^a-zA-Z0-9_-]+/g, '_');

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeBaseName}_clientes_com_erro.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <AlertTriangle size={24} className={styles.errorIcon} />
          <h2 className={styles.modalTitle}>Erros na Importação</h2>
          <button className={styles.closeBtn} onClick={onClose} title="Fechar">
            <X size={24} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <p>
            A importação foi concluída, mas alguns clientes não puderam ser processados.
            Abaixo está a lista de erros.
          </p>
          <div className={styles.errorListContainer}>
            <ul className={styles.errorList}>
              {errorDetails.slice(0, 10).map((error, index) => (
                <li key={index} className={styles.errorItem}>
                  <strong>Linha {error.row}:</strong> {error.message}
                  {error.email && ` (Email: ${error.email})`}
                </li>
              ))}
            </ul>
            {errorDetails.length > 10 && (
              <p className={styles.moreErrors}>
                ... e mais {errorDetails.length - 10} erro(s).
              </p>
            )}
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.downloadBtn} onClick={handleDownload}>
            <Download size={16} />
            Baixar Planilha com Erros
          </button>
          <button className={styles.cancelBtn} onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportErrorModal;
